/**
 * Phase 224 - the watermark geometry, mirrored from backend core/watermark.py `layout()` so the on-screen receipt
 * places the logo exactly like the PDF does: sizes are a percentage of the page width, the same seven anchors with
 * the same margin, and `repeat` is the same centred, staggered grid with a gap of half a logo.
 *
 * layoutTiles(pageW, pageH, logoW, logoH, settings) -> [{ x, y, w, h }] in the same unit as pageW / pageH.
 */
export const POSITIONS = ["center", "top", "bottom", "top-left", "top-right", "bottom-left", "bottom-right"];
export const POSITION_LABELS = {
  center: "Center", top: "Top", bottom: "Bottom", "top-left": "Top-left corner", "top-right": "Top-right corner",
  "bottom-left": "Bottom-left corner", "bottom-right": "Bottom-right corner",
};
export const DEFAULT_SETTINGS = { enabled: true, position: "center", repeat: "single", size_pct: 45, opacity_pct: 8, color_mode: "grey" };
export const OPACITY_MAX = 30;
export const OPACITY_MIN = 1;
export const SIZE_MIN = 10;
export const SIZE_MAX = 80;
const TILE_GAP = 0.5;
const MARGIN_RATIO = 28.35 / 595.27;   // 10 mm on an A4 page, as a share of the page width

const clamp = (v, lo, hi, dflt) => {
  const n = Math.round(Number(v));
  if (Number.isNaN(n)) return dflt;
  return Math.max(lo, Math.min(hi, n));
};

export function normalizeSettings(raw) {
  const s = { ...DEFAULT_SETTINGS, ...(raw || {}) };
  if (!POSITIONS.includes(s.position)) s.position = DEFAULT_SETTINGS.position;
  if (!["single", "repeat"].includes(s.repeat)) s.repeat = DEFAULT_SETTINGS.repeat;
  if (!["original", "grey"].includes(s.color_mode)) s.color_mode = DEFAULT_SETTINGS.color_mode;
  s.size_pct = clamp(s.size_pct, SIZE_MIN, SIZE_MAX, DEFAULT_SETTINGS.size_pct);
  s.opacity_pct = clamp(s.opacity_pct, OPACITY_MIN, OPACITY_MAX, DEFAULT_SETTINGS.opacity_pct);
  s.enabled = s.enabled !== false;
  return s;
}

export function layoutTiles(pageW, pageH, logoW, logoH, settings) {
  const s = normalizeSettings(settings);
  if (!pageW || !pageH) return [];
  const margin = pageW * MARGIN_RATIO;
  const ratio = logoW ? logoH / logoW : 1;
  let w = (pageW * s.size_pct) / 100;
  let h = w * ratio;
  const maxH = pageH - 2 * margin;
  if (h > maxH) { h = maxH; w = ratio ? h / ratio : w; }
  if (s.repeat === "single") {
    const pos = s.position;
    let x;
    if (pos.includes("left")) x = margin;
    else if (pos.includes("right")) x = pageW - margin - w;
    else x = (pageW - w) / 2;
    let y;   // CSS: y grows downwards (the PDF measures from the bottom - same anchors, mirrored)
    if (pos.startsWith("top")) y = margin;
    else if (pos.startsWith("bottom")) y = pageH - margin - h;
    else y = (pageH - h) / 2;
    return [{ x, y, w, h }];
  }
  const stepX = w * (1 + TILE_GAP);
  const stepY = h * (1 + TILE_GAP);
  const cols = Math.max(1, Math.floor((pageW - 2 * margin + w * TILE_GAP) / stepX));
  const rows = Math.max(1, Math.floor((pageH - 2 * margin + h * TILE_GAP) / stepY));
  const gridW = cols * stepX - w * TILE_GAP;
  const gridH = rows * stepY - h * TILE_GAP;
  const x0 = (pageW - gridW) / 2;
  const y0 = (pageH - gridH) / 2;
  const out = [];
  for (let r = 0; r < rows; r += 1) {
    const shift = r % 2 === 1 && cols > 1 ? stepX / 2 : 0;
    for (let c = 0; c < cols; c += 1) {
      const x = x0 + c * stepX + shift;
      if (x + w > pageW - margin + 0.5) continue;
      // PDF rows are counted from the bottom; mirror so the staggered rows match top-to-bottom
      const rowFromTop = rows - 1 - r;
      out.push({ x, y: y0 + rowFromTop * stepY, w, h });
    }
  }
  return out;
}
